/**
 * The one place browser adapters are constructed.
 *
 * Everything below is a `new` on a class from `apps/web/src/platform/**`, or —
 * for the one port whose implementation needs no platform at all — a factory
 * call into `@ff/store`. There is no logic here on purpose: the moment this file starts deciding *which*
 * adapter to build, it becomes a second capability-detection layer competing
 * with the one each adapter already performs internally (each of them takes an
 * environment record and degrades on its own).
 *
 * Called once, at module scope of `App`, so the whole app shares one visibility
 * subscription, one leader participant identity, and one wake-lock sentinel.
 * Building a second bundle would not fail — it would quietly double them.
 */

import { createMediaDigest } from "@ff/store";
import { BrowserClock } from "../platform/clock";
import { BrowserExport } from "../platform/export";
import { browserIds } from "../platform/identity";
import { BrowserLeaderElection } from "../platform/leader";
import { BrowserPreferences, BrowserSceneStorage } from "../platform/preferences";
import { BrowserTicker } from "../platform/ticker";
import { BrowserVisibility } from "../platform/visibility";
import { BrowserWakeLock } from "../platform/wakeLock";
import type { AppPorts } from "./ports";

export function createBrowserPorts(): AppPorts {
  return {
    clock: new BrowserClock(),
    ticker: new BrowserTicker(),
    visibility: new BrowserVisibility(),
    wakeLock: new BrowserWakeLock(),
    leaderElection: new BrowserLeaderElection(),
    preferences: new BrowserPreferences(),
    sceneStorage: new BrowserSceneStorage(),
    exporter: new BrowserExport(),
    // A factory rather than a `new`, because the adapter resolves `crypto` once
    // at construction and reports which source it settled on; two of them would
    // be two answers to that question.
    ids: browserIds(),
    // Not a browser adapter: `createMediaDigest` is `@ff/store`'s pure
    // streaming SHA-256. It is constructed here anyway because this is the one
    // place the bundle is assembled, and a second construction site is how two
    // halves of the app end up hashing with two different implementations.
    digest: createMediaDigest(),
  };
}
